// The cashier picker's decisions, kept out of the component so they can be
// tested directly (cashierPickerUtils.test.ts).
//
// The roster (backend routers/cashiers.py) is the list a CASH payment's
// cashierName/cashierPhone are picked FROM -- the payment itself still stores
// those two values, exactly as before, so nothing downstream changes.

import { cashierService } from '../../services/api';
import { Cashier } from '../../types';


// The roster, fetched once per session and cached in module scope: the three
// payment forms mount repeatedly and the list changes rarely. BankNameInput
// caches the bank list the same way.
//
// It lives in this plain module rather than in CashierPicker.tsx because the
// roster screen invalidates it after an add or an edit -- a screen should not
// have to import a React component to reach a cache.
let cashiersCache: Cashier[] | null = null;
let fetchPromise: Promise<Cashier[]> | null = null;

/** The active roster. Only active cashiers: a deactivated one must not be
 * selectable for a new collection, while the payments already naming them stay
 * readable. */
export function loadCashiers(): Promise<Cashier[]> {
    if (cashiersCache) return Promise.resolve(cashiersCache);
    if (fetchPromise) return fetchPromise;
    fetchPromise = cashierService.getCashiers({ activeOnly: true })
        .then(res => {
            const list = res.data?.cashiers || [];
            cashiersCache = list;
            return list;
        })
        // Deliberately does NOT cache the failure: an empty list stored here
        // would leave the dropdown empty for the rest of the session over one
        // network blip. Clearing the promise lets the next mount try again.
        .catch(() => { fetchPromise = null; return []; });
    return fetchPromise;
}

/** Drop the cache so the next read refetches -- called by the roster screen
 * after adding or editing a cashier, otherwise a payment form opened in the
 * same session would keep offering the stale list. */
export const invalidateCashierCache = () => { cashiersCache = null; fetchPromise = null; };

/** The cached roster as it stands, for a first render with no flash of empty. */
export const cachedCashiers = (): Cashier[] => cashiersCache || [];

/** Digits-only form of a phone number, for comparison and search. */
export const phoneDigits = (raw: string | null | undefined): string =>
  (raw || '').replace(/\D/g, '');

/**
 * Roster entries matching what the user typed.
 *
 * A query is tried BOTH as text (against the name) and digits-only (against
 * the number), because someone searching for a cashier types the number the
 * way they would dial it -- '91339-57450' has to find the row stored as
 * '9133957450'. An empty query is everyone, not nobody.
 */
export const filterCashiers = (cashiers: Cashier[], query: string): Cashier[] => {
  const text = (query || '').trim().toLowerCase();
  if (!text) return cashiers;
  const digits = phoneDigits(query);
  return cashiers.filter((c) => {
    if ((c.cashierName || '').toLowerCase().includes(text)) return true;
    // Compared on the last 10 digits so a roster number stored with a country
    // code is still found by the bare number, and vice versa.
    if (!digits) return false;
    const stored = phoneDigits(c.cashierPhoneNumber);
    return stored.includes(digits) || stored.slice(-10).includes(digits.slice(-10));
  });
};

/** How one roster entry reads in the dropdown. */
export const cashierLabel = (cashier: Cashier): string =>
  cashier.cashierPhoneNumber
    ? `${cashier.cashierName} · ${cashier.cashierPhoneNumber}`
    : cashier.cashierName;

/**
 * The roster entry a payment's stored name/phone refers to, or null.
 *
 * Matched on the PHONE, not the name: a name corrected on the roster after a
 * payment was saved must not make that payment look hand-typed. Without this,
 * reopening a saved CASH payment would show the cashier as free text and a
 * re-save would silently lose the roster link.
 *
 * The last 10 digits decide, so a country code on one side only is not a
 * mismatch -- the same rule utils/cashiers.py same_phone applies server-side.
 */
export const matchRosterEntry = (
  cashiers: Cashier[],
  _name: string,
  phone: string,
): Cashier | null => {
  const digits = phoneDigits(phone);
  if (digits.length < 10) return null;
  const tail = digits.slice(-10);
  return cashiers.find((c) => phoneDigits(c.cashierPhoneNumber).slice(-10) === tail) || null;
};
